import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";
import { z } from "zod";
import { migratePatchDocument, type PatchDocument } from "../domain/patch";
import type { AttachmentBinary } from "../persistence/database";

export const BUNDLE_FORMAT_VERSION = 1;
export const PATCH_BUNDLE_EXTENSION = ".patchdoc";
export const LIBRARY_BUNDLE_EXTENSION = ".patchbackup";

const MAX_BUNDLE_BYTES = 100 * 1024 * 1024;
const MAX_ENTRIES = 5000;
const MAX_DOCUMENT_BYTES = 5 * 1024 * 1024;

const checksumSchema = z.string().regex(/^[a-f0-9]{64}$/);
const attachmentFileSchema = z.object({
  id: z.string().min(1),
  file: z.string().regex(/^attachments\/[a-zA-Z0-9._-]+$/),
  mimeType: z.string().min(1),
  size: z.number().int().nonnegative(),
});
const commonManifestSchema = z.object({
  bundleFormatVersion: z.literal(BUNDLE_FORMAT_VERSION),
  application: z.literal("Patchwork"),
  applicationVersion: z.string().min(1),
  exportedAt: z.string().datetime(),
  requiredInstrumentDefinitionIds: z.array(z.string().min(1)),
  checksums: z.record(z.string(), checksumSchema),
  attachmentFiles: z.array(attachmentFileSchema).optional(),
});

const patchManifestSchema = commonManifestSchema.extend({
  kind: z.literal("patch"),
  patchId: z.string().min(1),
  title: z.string().min(1),
  patchFile: z.literal("patch.json"),
});

const libraryManifestSchema = commonManifestSchema.extend({
  kind: z.literal("library"),
  patchCount: z.number().int().nonnegative(),
  patchFiles: z.array(z.string().regex(/^patches\/[a-zA-Z0-9._-]+\.json$/)),
  activePatchId: z.string().min(1).optional(),
  settingsFile: z.literal("settings.json").optional(),
});

const bundleManifestSchema = z.discriminatedUnion("kind", [
  patchManifestSchema,
  libraryManifestSchema,
]);

export type BundleManifest = z.infer<typeof bundleManifestSchema>;

export type ParsedBundle = {
  manifest: BundleManifest;
  documents: PatchDocument[];
  applicationSettings?: unknown;
  attachments: AttachmentBinary[];
};

function jsonBytes(value: unknown) {
  return strToU8(`${JSON.stringify(value, null, 2)}\n`);
}

async function sha256(bytes: Uint8Array) {
  if (!globalThis.crypto?.subtle) throw new Error("This browser cannot verify bundle checksums.");
  const copy = Uint8Array.from(bytes);
  const digest = await globalThis.crypto.subtle.digest("SHA-256", copy.buffer);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function checksumsFor(files: Record<string, Uint8Array>) {
  const entries = await Promise.all(
    Object.entries(files).map(async ([name, bytes]) => [name, await sha256(bytes)] as const),
  );
  return Object.fromEntries(entries);
}

function requiredDefinitions(documents: PatchDocument[]) {
  return [
    ...new Set(
      documents.flatMap((document) =>
        document.workspace.nodes.flatMap((node) =>
          node.kind === "instrument" ? [node.definitionId] : [],
        ),
      ),
    ),
  ].sort();
}

function encodedBundle(manifest: BundleManifest, files: Record<string, Uint8Array>) {
  return zipSync({ "manifest.json": jsonBytes(manifest), ...files }, { level: 6 });
}

async function attachmentFiles(
  documents: PatchDocument[],
  binaries: AttachmentBinary[],
): Promise<{
  files: Record<string, Uint8Array>;
  manifestEntries: z.infer<typeof attachmentFileSchema>[];
}> {
  const references = new Map(
    documents.flatMap((document) =>
      document.attachments.map((attachment) => [attachment.id, attachment] as const),
    ),
  );
  const binaryById = new Map(binaries.map((attachment) => [attachment.id, attachment.blob]));
  const files: Record<string, Uint8Array> = {};
  const manifestEntries: z.infer<typeof attachmentFileSchema>[] = [];
  for (const [id, reference] of references) {
    const blob = binaryById.get(id);
    if (!blob) throw new Error(`Attachment '${reference.filename}' is missing from local storage.`);
    const file = `attachments/${safeBundleFilename(id, "")}-${safeBundleFilename(reference.filename, "")}`;
    const bytes = new Uint8Array(await blob.arrayBuffer());
    files[file] = bytes;
    manifestEntries.push({
      id,
      file,
      mimeType: reference.mimeType,
      size: bytes.byteLength,
    });
  }
  return { files, manifestEntries };
}

export async function createPatchBundle(
  document: PatchDocument,
  applicationVersion = "0.1.0",
  binaries: AttachmentBinary[] = [],
) {
  const validated = migratePatchDocument(document);
  const attachments = await attachmentFiles([validated], binaries);
  const files = { "patch.json": jsonBytes(validated), ...attachments.files };
  const manifest: BundleManifest = {
    bundleFormatVersion: BUNDLE_FORMAT_VERSION,
    kind: "patch",
    application: "Patchwork",
    applicationVersion,
    exportedAt: new Date().toISOString(),
    patchId: document.id,
    title: document.title,
    patchFile: "patch.json",
    requiredInstrumentDefinitionIds: requiredDefinitions([document]),
    attachmentFiles: attachments.manifestEntries,
    checksums: await checksumsFor(files),
  };
  return encodedBundle(manifest, files);
}

export async function createLibraryBundle(
  documents: PatchDocument[],
  options: {
    activePatchId?: string;
    applicationSettings?: unknown;
    applicationVersion?: string;
    attachments?: AttachmentBinary[];
  } = {},
) {
  const validated = documents.map(migratePatchDocument);
  const ids = new Set(validated.map((document) => document.id));
  if (ids.size !== validated.length) throw new Error("Library contains duplicate patch IDs.");

  const files: Record<string, Uint8Array> = {};
  const patchFiles = validated.map((document, index) => {
    const safeId = safeBundleFilename(document.id, "");
    const name = `patches/${String(index + 1).padStart(4, "0")}-${safeId}.json`;
    files[name] = jsonBytes(document);
    return name;
  });
  if (options.applicationSettings !== undefined) {
    files["settings.json"] = jsonBytes(options.applicationSettings);
  }
  const attachments = await attachmentFiles(validated, options.attachments ?? []);
  Object.assign(files, attachments.files);

  const manifest: BundleManifest = {
    bundleFormatVersion: BUNDLE_FORMAT_VERSION,
    kind: "library",
    application: "Patchwork",
    applicationVersion: options.applicationVersion ?? "0.1.0",
    exportedAt: new Date().toISOString(),
    patchCount: validated.length,
    patchFiles,
    activePatchId: options.activePatchId,
    settingsFile: options.applicationSettings === undefined ? undefined : "settings.json",
    requiredInstrumentDefinitionIds: requiredDefinitions(validated),
    attachmentFiles: attachments.manifestEntries,
    checksums: await checksumsFor(files),
  };
  return encodedBundle(manifest, files);
}

function parseJson(bytes: Uint8Array, filename: string): unknown {
  if (bytes.byteLength > MAX_DOCUMENT_BYTES) throw new Error(`${filename} is unexpectedly large.`);
  try {
    return JSON.parse(strFromU8(bytes));
  } catch {
    throw new Error(`${filename} does not contain valid JSON.`);
  }
}

async function inputBytes(input: Blob | Uint8Array | ArrayBuffer) {
  if (input instanceof Uint8Array) return input;
  if (input instanceof ArrayBuffer) return new Uint8Array(input);
  return new Uint8Array(await input.arrayBuffer());
}

export async function readBundle(input: Blob | Uint8Array | ArrayBuffer): Promise<ParsedBundle> {
  const bytes = await inputBytes(input);
  if (bytes.byteLength > MAX_BUNDLE_BYTES)
    throw new Error("Bundle exceeds the 100 MB safety limit.");

  let archive: Record<string, Uint8Array>;
  try {
    archive = unzipSync(bytes);
  } catch {
    throw new Error("The selected file is not a readable Patchwork bundle.");
  }
  if (Object.keys(archive).length > MAX_ENTRIES) throw new Error("Bundle contains too many files.");
  const manifestBytes = archive["manifest.json"];
  if (!manifestBytes) throw new Error("Bundle is missing manifest.json.");

  const parsedManifest = bundleManifestSchema.safeParse(parseJson(manifestBytes, "manifest.json"));
  if (!parsedManifest.success) throw new Error("Bundle manifest is invalid or unsupported.");
  const manifest = parsedManifest.data;

  for (const [filename, expected] of Object.entries(manifest.checksums)) {
    const file = archive[filename];
    if (!file) throw new Error(`Bundle is missing ${filename}.`);
    if ((await sha256(file)) !== expected)
      throw new Error(`${filename} failed its integrity check.`);
  }

  const patchFiles = manifest.kind === "patch" ? [manifest.patchFile] : manifest.patchFiles;
  const integrityFiles = [
    ...patchFiles,
    ...(manifest.attachmentFiles?.map((attachment) => attachment.file) ?? []),
    ...(manifest.kind === "library" && manifest.settingsFile ? [manifest.settingsFile] : []),
  ];
  for (const filename of integrityFiles) {
    if (!manifest.checksums[filename]) {
      throw new Error(`${filename} is not covered by the integrity manifest.`);
    }
  }
  const documents = patchFiles.map((filename) => {
    const file = archive[filename];
    if (!file) throw new Error(`Bundle is missing ${filename}.`);
    try {
      return migratePatchDocument(parseJson(file, filename));
    } catch (error) {
      throw new Error(
        `${filename} is not a supported patch document: ${error instanceof Error ? error.message : String(error)}`,
        { cause: error },
      );
    }
  });

  if (manifest.kind === "patch" && documents[0]?.id !== manifest.patchId) {
    throw new Error("Patch ID does not match the bundle manifest.");
  }
  if (manifest.kind === "library") {
    if (documents.length !== manifest.patchCount)
      throw new Error("Library patch count is invalid.");
    if (new Set(documents.map((document) => document.id)).size !== documents.length) {
      throw new Error("Library contains duplicate patch IDs.");
    }
  }

  const settingsBytes =
    manifest.kind === "library" && manifest.settingsFile
      ? archive[manifest.settingsFile]
      : undefined;
  const attachments = (manifest.attachmentFiles ?? []).map((attachment) => {
    const file = archive[attachment.file];
    if (!file) throw new Error(`Bundle is missing ${attachment.file}.`);
    if (file.byteLength !== attachment.size) {
      throw new Error(`${attachment.file} does not match its recorded size.`);
    }
    return {
      id: attachment.id,
      blob: new Blob([Uint8Array.from(file)], { type: attachment.mimeType }),
    };
  });
  return {
    manifest,
    documents,
    applicationSettings: settingsBytes
      ? parseJson(
          settingsBytes,
          manifest.kind === "library" ? manifest.settingsFile! : "settings.json",
        )
      : undefined,
    attachments,
  };
}

export function safeBundleFilename(title: string, extension: string) {
  const stem = title
    .normalize("NFKD")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase()
    .slice(0, 80);
  return `${stem || "patchwork-export"}${extension}`;
}
